import { CheckboxItem, getTristateValue, TristateValue } from './checkbox-item'

export class CheckboxTreeItem {
  // Used for filtering
  readonly normalizedName: string

  readonly value: TristateValue
  readonly filterValue: TristateValue
  readonly shownChildrenCount: number
  readonly selectedChildrenIds: string[] = []

  constructor(
    readonly title: string,
    readonly id: string,
    readonly items: CheckboxItem[],
    readonly hidden: boolean = false,
    readonly disabled: boolean = false,
  ) {
    this.normalizedName = title.toLowerCase().trim()
    this.value = this.getValue()
    this.filterValue = this.getFilterValue()
    this.shownChildrenCount = this.getShowChildrenCount()
    this.selectedChildrenIds = this.getSelectedChildrenIds()
  }

  collapsed = true

  /**
   * Used for filtering. Must be normalized already.
   *
   * If we match the filter, all children are visible,
   *
   * Otherwise:
   * If any of the children match the filter, the parent is visible
   * If none of the children are visible, the parent is hidden
   */
  withFilter(normalizedFilter: string): CheckboxTreeItem {
    const parentMatchesFilter = this.normalizedName.includes(normalizedFilter)
    if (parentMatchesFilter) {
      return this.withAllChildrenShown()
    } else {
      return this.withMatchingChildrenShown(normalizedFilter)
    }
  }

  private withMatchingChildrenShown(normalizedFilter: string): CheckboxTreeItem {
    const items = this.items.map(i => i.withFilter(normalizedFilter))
    const shownChildCount = items.reduce((acc, i) => acc + (i.hidden ? 0 : 1), 0)
    const checkboxListItem = new CheckboxTreeItem(this.title, this.id, items, shownChildCount === 0, this.disabled)
    checkboxListItem.collapsed = shownChildCount === 0
    return checkboxListItem
  }

  private withAllChildrenShown(): CheckboxTreeItem {
    this.collapsed = false
    return new CheckboxTreeItem(
      this.title,
      this.id,
      this.items.map(i => i.withHidden(false)),
      false,
      this.disabled,
    )
  }

  getShowChildren(): CheckboxItem[] {
    return this.items.filter(x => !x.hidden)
  }

  isSelected(): boolean {
    return this.items.every(x => x.selected)
  }

  getShowChildrenCount(): number {
    return this.getShowChildren().length
  }

  private getSelectedChildrenIds(): string[] {
    return this.getShowChildren()
      .filter(child => child.selected)
      .map(child => child.id)
  }

  private getValue(): TristateValue {
    const selectedCount = this.items.filter(x => x.selected).length
    return getTristateValue(selectedCount, this.items.length)
  }

  private getFilterValue(): TristateValue {
    const shownItems = this.items.filter(x => !x.hidden)
    const selectedShownItems = shownItems.filter(x => x.selected)
    return getTristateValue(selectedShownItems.length, shownItems.length)
  }
}
